'use client';

import {
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Drawer as DrawerUI,
} from '@/components/ui/Drawer/components/DrawerUI';
import { cn } from '@/libs';
import { useDrawerStore, useModalStore } from '@/stores';

import { Icon } from '../Icon';

export const Drawer = () => {
  const drawer = useDrawerStore((state) => state.drawers[0]);
  const setDrawerOpen = useDrawerStore((state) => state.setDrawerOpen);
  const removeDrawer = useDrawerStore((state) => state.removeDrawer);
  const preventDrawer = useDrawerStore((state) => state.preventingComponent);
  const hasOpenModal = useModalStore((state) => state.modals.length > 0);

  if (!drawer) return null;

  const tempDisablePreventClose = drawer.tempDisablePreventClose;

  const {
    id,
    isOpen,
    Component,
    content,
    title,
    description,
    headerContent,
    footerContent,
    hasHeader = true,
    hardClose,

    drawerOptions = {},
    drawerOverlayOptions,
    drawerContentOptions = {},
    drawerHeaderOptions,
    drawerTitleOptions,
    drawerDescriptionOptions,
    drawerFooterOptions,
  } = drawer;

  const {
    fadeFromIndex,
    snapPoints,
    dismissible = true,
    modal = true,
    direction,
    mobileDirection,
    ...restDrawerOptions
  } = drawerOptions;
  const extraDrawerProps =
    fadeFromIndex !== undefined && snapPoints
      ? { fadeFromIndex, snapPoints, ...restDrawerOptions }
      : restDrawerOptions;

  const Body = typeof Component === 'function' ? <Component /> : (Component ?? content);

  const requestClose = () =>
    !hardClose && !tempDisablePreventClose && setDrawerOpen(id, false);

  return (
    <DrawerUI
      key={id}
      open={isOpen}
      onOpenChange={(o) => !o && requestClose()}
      repositionInputs={false}
      dismissible={dismissible && !hasOpenModal}
      modal={modal}
      direction={direction}
      mobileDirection={mobileDirection}
      {...extraDrawerProps}
    >
      <DrawerContent
        data-cy={`drawer-${drawer.drawerType || 'default'}`}
        className={cn('p-4 pb-8 max-h-[90vh] md:max-h-screen')}
        {...drawerContentOptions}
        drawerOverlayOptions={drawerOverlayOptions}
        direction={direction}
        mobileDirection={mobileDirection}
        onAnimationEnd={(e) => {
          if (e.currentTarget.getAttribute('data-state') === 'closed') {
            if (!preventDrawer) {
              removeDrawer(id);
            }
            drawerContentOptions.onAnimationEnd?.(e);
          }
        }}
      >
        {hasHeader && (title || description || headerContent) && (
          <DrawerHeader {...drawerHeaderOptions} data-cy="drawer-header">
            {headerContent}
            <div className="relative">
              {title && (
                <DrawerTitle {...drawerTitleOptions} data-cy="drawer-title">
                  {title}
                </DrawerTitle>
              )}
              <DrawerClose
                key={`close-${id}`}
                data-cy="drawer-close-button"
                className={cn(
                  'absolute rounded-full bg-foreground/5 flex items-center justify-center outline-none ring-foreground/30 ring-offset-background ring-offset-2 focus-visible:ring-2 right-0 top-1/2 -translate-y-1/2 h-7 w-7 z-10',
                  !dismissible ? 'hidden' : '',
                )}
              >
                <Icon.Close width={10} height={10} />
              </DrawerClose>
            </div>
            {description && (
              <DrawerDescription
                {...drawerDescriptionOptions}
                data-cy="drawer-description"
              >
                {description}
              </DrawerDescription>
            )}
          </DrawerHeader>
        )}
        {!hasHeader && title && (
          <DrawerTitle {...drawerTitleOptions} data-cy="drawer-title">
            {title}
          </DrawerTitle>
        )}

        {Body}

        {footerContent && (
          <DrawerFooter {...drawerFooterOptions} data-cy="drawer-footer">
            {footerContent}
          </DrawerFooter>
        )}
      </DrawerContent>

      {preventDrawer && (
        <div className="fixed inset-0 z-[2000] bg-black/70 flex items-end justify-center">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-background rounded-lg p-4 shadow-lg"
          >
            {preventDrawer}
          </div>
        </div>
      )}
    </DrawerUI>
  );
};
